/**
 * Pickers narrowed by earlier answers (BILL-002, BILL-024, BILL-034).
 *
 * The reference cascades its pickers: a purchase invoice lists only the chosen
 * supplier's orders and says "No entries" before one is chosen; a sales
 * invoice lists only the chosen customer's projects, and only that project's
 * open, invoice-type billing requests. Here every document picker listed every
 * approved document of its type, whoever it was with.
 *
 * A field says what narrows it (`FieldDef.lookup.narrow`): a parameter, and
 * either the form field that answers it or a fixed value. The client turns
 * that into query parameters from the values on the form; the lookups
 * endpoint turns those into conditions, for every document type and for the
 * project master alike.
 */
import type { FieldDef, LookupNarrowing } from './types.js'

/** A party's documents, or — on the project master — the customer's projects. */
export const byParty = (field: string): LookupNarrowing => ({ param: 'party', field })
/** One project's documents. */
export const byProject = (field = 'projectId'): LookupNarrowing => ({ param: 'project', field })
/** Of one kind — a billing request's Invoice or Credit note. */
export const ofKind = (value: string): LookupNarrowing => ({ param: 'kind', value })
/** Only documents with something still left to raise against them. */
export const stillOpen = (): LookupNarrowing => ({ param: 'open', value: '1' })

/** Adds narrowing to a lookup field. */
export const narrowed = (field: FieldDef, ...narrow: LookupNarrowing[]): FieldDef => ({
  ...field,
  lookup: { ...(field.lookup ?? { entity: '' }), narrow },
})

/** Several records in one lookup — two purchase orders on one invoice (BILL-005). */
export const many = (field: FieldDef): FieldDef => ({
  ...field,
  lookup: { ...(field.lookup ?? { entity: '' }), multiple: true },
})

/**
 * The ids a lookup value holds. A multi-select stores an array; a single one a
 * string; a document saved before a field became multiple still holds a string.
 */
export const idsOf = (value: unknown): string[] => {
  if (typeof value === 'string') return value === '' ? [] : [value]
  if (Array.isArray(value)) return value.filter((v): v is string => typeof v === 'string' && v !== '')
  return []
}

/** The first id a lookup value holds, or null. */
export const firstIdOf = (value: unknown): string | null => idsOf(value)[0] ?? null

/**
 * The query parameters a narrowed lookup sends, from the values on the form.
 *
 * `undefined` for a field nothing narrows; `null` when something it depends
 * on has not been answered yet — the picker then says so rather than listing
 * everything.
 */
export const lookupNarrowing = (
  field: Pick<FieldDef, 'lookup'>,
  values: Readonly<Record<string, unknown>>,
): Record<string, string> | null | undefined => {
  const narrow = field.lookup?.narrow
  if (!narrow || narrow.length === 0) return undefined
  const out: Record<string, string> = {}
  for (const n of narrow) {
    if (n.value !== undefined) { out[n.param] = n.value; continue }
    // A project narrowing takes every project the field names (INV-007);
    // the rest narrow by the first record.
    const id = n.field ? (n.param === 'project' ? idsOf(values[n.field]).join(',') || null : firstIdOf(values[n.field])) : null
    if (id === null) return null
    out[n.param] = id
  }
  return out
}

/** The form fields a narrowed lookup depends on — for "Choose the supplier first". */
export const narrowingFields = (field: Pick<FieldDef, 'lookup'>): string[] =>
  (field.lookup?.narrow ?? []).flatMap((n) => (n.field ? [n.field] : []))
